<template>
  <v-row v-if="siteConfig.contact_form">
    <v-col>
      <v-row v-if="isSubmitted">
        <v-col>
          <v-alert
            :color="isSubmitError ? 'error' : 'success'"
            :type="isSubmitError ? 'error' : 'success'"
          >
            <span v-if="isSubmitError">
              Sorry! Our contact form may be having a bad day. Please
              <a :href="formMailToHref" class="text-white" target="_blank"
                >contact us via email</a
              >, or perhaps try a bit later.</span
            ><span v-else-if="siteConfig.contact_form.success_text">
              {{ siteConfig.contact_form.success_text }}
            </span>
          </v-alert>
        </v-col>
      </v-row>

      <v-form v-else ref="form" v-model="isValid" lazy-validation>
        <v-row class="pa-2">
          <v-col
            v-if="shownFields.firstName && (!multiStep || step === 0)"
            cols="12"
            :md="shownFields.firstName && shownFields.lastName ? 6 : 12"
          >
            <v-text-field
              id="contact_form_firstname"
              ref="firstname"
              v-model.trim="formData.firstName"
              :label="requiredFields.firstName ? 'First Name *' : 'First Name'"
              :rules="requiredFields.firstName ? [requiredRule] : []"
              density="compact"
              hide-details="auto"
              variant="outlined"
            ></v-text-field>
          </v-col>

          <v-col
            v-if="shownFields.lastName && (!multiStep || step === 0)"
            cols="12"
            :md="shownFields.firstName && shownFields.lastName ? 6 : 12"
          >
            <v-text-field
              id="contact_form_lastname"
              ref="lastname"
              v-model.trim="formData.lastName"
              :label="requiredFields.lastName ? 'Last Name *' : 'Last Name'"
              :rules="requiredFields.lastName ? [requiredRule] : []"
              density="compact"
              hide-details="auto"
              variant="outlined"
            ></v-text-field>
          </v-col>

          <v-col
            v-if="shownFields.email && (!multiStep || step === 0)"
            cols="12"
            md="6"
          >
            <v-text-field
              id="contact_form_email"
              ref="email"
              v-model.trim="formData.email"
              :rules="[requiredRule, emailRule]"
              density="compact"
              hide-details="auto"
              label="Email *"
              type="email"
              variant="outlined"
            ></v-text-field>
          </v-col>

          <v-col
            v-if="shownFields.phone && (!multiStep || step === 0)"
            cols="12"
            md="6"
          >
            <v-text-field
              id="contact_form_phone"
              ref="phone"
              v-model.trim="formData.phone"
              :label="requiredFields.phone ? 'Phone Number *' : 'Phone Number'"
              :rules="requiredFields.phone ? [requiredRule] : []"
              density="compact"
              hide-details="auto"
              variant="outlined"
            ></v-text-field>
          </v-col>

          <v-col
            v-if="shownFields.inquiryType && (!multiStep || step === 0)"
            cols="12"
          >
            <v-select
              id="contact_form_inquiry_type_select"
              v-model="formData.inquiryTypeSelect"
              :items="inquiryTypeItems"
              :rules="[requiredRule]"
              hide-details="auto"
              label="How can we help you? *"
              required
              variant="outlined"
            />
          </v-col>

          <v-col
            v-if="
              shownFields.support &&
              formData.inquiryTypeSelect === 'support' &&
              (!multiStep || step === 1)
            "
            cols="12"
          >
            <v-select
              id="contact_form_support_select"
              v-model="formData.supportSelect"
              :items="supportItems"
              :menu-props="{ maxHeight: 500 }"
              :rules="[requiredRule]"
              hide-details="auto"
              label="Select the type of support you need. *"
              required
              variant="outlined"
            />
          </v-col>

          <v-col
            v-if="
              shownFields.support &&
              formData.inquiryTypeSelect === 'support' &&
              (!multiStep || step === 1)
            "
            cols="12"
          >
            <v-textarea
              id="contact_form_support_freeform"
              ref="supportFreeform"
              v-model="formData.supportFreeform"
              :rules="[requiredRule]"
              density="compact"
              hide-details="auto"
              label="Please provide details so we can best assist you. *"
              rows="3"
              variant="outlined"
            ></v-textarea>
          </v-col>

          <v-col
            v-if="
              shownFields.company &&
              formData.inquiryTypeSelect === 'general' &&
              (!multiStep || step === 1)
            "
            cols="12"
            :md="shownFields.company && shownFields.jobTitle ? 6 : 12"
          >
            <v-text-field
              id="contact_form_company"
              ref="company"
              v-model.trim="formData.company"
              :rules="requiredFields.company ? [requiredRule] : []"
              density="compact"
              hide-details="auto"
              label="Company Name *"
              variant="outlined"
            ></v-text-field>
          </v-col>

          <v-col
            v-if="
              shownFields.jobTitle &&
              formData.inquiryTypeSelect === 'general' &&
              (!multiStep || step === 1)
            "
            cols="12"
            :md="shownFields.company && shownFields.jobTitle ? 6 : 12"
          >
            <v-text-field
              id="contact_form_jobtitle"
              ref="jobtitle"
              v-model.trim="formData.jobTitle"
              :label="requiredFields.jobTitle ? 'Job Title *' : 'Job Title'"
              :rules="requiredFields.jobTitle ? [requiredRule] : []"
              density="compact"
              hide-details="auto"
              variant="outlined"
            ></v-text-field>
          </v-col>

          <v-col
            v-if="
              shownFields.inquiryProduct &&
              formData.inquiryTypeSelect === 'general' &&
              (!multiStep || step === 1)
            "
            cols="12"
          >
            <v-select
              id="contact_form_inquiry_product_select"
              v-model="formData.inquiryProductSelect"
              :items="inquiryProductItems"
              :menu-props="{ maxHeight: 500 }"
              :rules="[requiredRule]"
              hide-details="auto"
              label="What product(s) are you interested in? *"
              multiple
              required
              variant="outlined"
            />

            <v-textarea
              v-if="formData.inquiryProductSelect.includes('other')"
              id="contact_form_inquiry_product_freeform"
              ref="inquiryProductFreeform"
              v-model="formData.inquiryProductFreeform"
              :rules="[requiredRule]"
              class="mt-3"
              density="compact"
              hide-details="auto"
              label="Explain what product(s) you are interested in. *"
              rows="3"
            ></v-textarea>
          </v-col>

          <v-col
            v-if="
              shownFields.marketingSource &&
              formData.inquiryTypeSelect === 'general' &&
              (!multiStep || step === 2)
            "
            cols="12"
          >
            <v-select
              id="contact_form_marketing_source_select"
              v-model="formData.marketingSourceSelect"
              :items="marketingSourceItems"
              :menu-props="{ maxHeight: 500 }"
              hide-details="auto"
              label="How did you hear about us?"
              variant="outlined"
            />

            <v-textarea
              v-if="formData.marketingSourceSelect === 'other'"
              id="contact_form_marketing_source_freeform"
              ref="marketingSourceFreeform"
              v-model.trim="formData.marketingSourceFreeform"
              class="mt-3"
              density="compact"
              hide-details="auto"
              label="Explain how you heard about us."
              rows="3"
            ></v-textarea>
          </v-col>

          <v-col
            v-if="
              shownFields.inquiry &&
              formData.inquiryTypeSelect === 'general' &&
              (!multiStep || step === 2)
            "
            cols="12"
          >
            <v-textarea
              id="contact_form_inquiry_freeform"
              ref="inquiryFreeform"
              v-model="formData.inquiryFreeform"
              :rules="[requiredRule]"
              density="compact"
              hide-details="auto"
              label="Please provide details so we can best assist you. *"
              rows="3"
              variant="outlined"
            ></v-textarea>
          </v-col>

          <v-col
            v-if="
              multiStep &&
              (formData.inquiryTypeSelect === null ||
                (formData.inquiryTypeSelect === 'support' && step < 1) ||
                (formData.inquiryTypeSelect === 'general' && step < 2))
            "
            cols="12"
            align="center"
          >
            <v-btn
              v-if="step > 0"
              class="text-none ma-1"
              color="primary"
              rounded="pill"
              size="large"
              variant="outlined"
              @click="stepBack"
              >Back</v-btn
            >
            <v-btn
              :disabled="!isValid"
              class="text-none ma-1"
              color="primary"
              rounded="pill"
              size="large"
              @click="stepNext"
              >Next</v-btn
            >
          </v-col>

          <v-col v-else cols="12">
            <v-row v-if="agreement" dense>
              <v-col cols="auto">
                <v-checkbox
                  v-model="formData.agreed"
                  :rules="agreement.is_consent_required ? [trueRule] : []"
                  density="compact"
                  hide-details
                ></v-checkbox>
              </v-col>

              <v-col>
                <!-- eslint-disable vue/no-v-html -->
                <div class="text-body-2" v-html="agreement.description"></div>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col class="text-center" cols="12">
                <p class="text-caption mb-2">
                  {{ siteConfig.contact_form.consent_text }}
                </p>

                <v-btn
                  v-if="multiStep"
                  class="text-none ma-1"
                  color="primary"
                  rounded="pill"
                  size="large"
                  variant="outlined"
                  @click="stepBack"
                  >Back</v-btn
                >

                <v-btn
                  :disabled="!isValid"
                  class="text-none ma-1"
                  color="primary"
                  rounded="pill"
                  size="large"
                  @click="submit"
                  >{{ submitText }}</v-btn
                >
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { tracker } from '#root/lib/tracker'
import { usePageContext } from '#root/renderer/usePageContext'
import { emailRule, requiredRule, trueRule } from '#root/lib/form-rules'
import { mailToHref } from '#root/lib/utils'
import { fnSubmitContactForm } from '#root/rpc/client/lead'

const FIELDS_DEFAULT = {
  company: true,
  firstName: true,
  lastName: true,
  jobTitle: false,
  email: true,
  phone: true,
  inquiryType: true,
  inquiry: true,
  inquiryProduct: true,
  marketingSource: true,
  support: true
}
const REQUIRED_DEFAULT = {
  company: true,
  firstName: true,
  lastName: false,
  jobTitle: true,
  phone: true
}

type ContactField = {
  key: FormDataKey
  shownKey: string
  spec: {
    name: string
    objectTypeId?: string
  }
  transform?: (value: unknown) => unknown
}

const EMAIL_FIELDS: ContactField[] = [
  {
    key: 'firstName',
    shownKey: 'firstName',
    spec: {
      name: 'First Name'
    }
  },
  {
    key: 'lastName',
    shownKey: 'lastName',
    spec: {
      name: 'Last Name'
    }
  },
  {
    key: 'jobTitle',
    shownKey: 'jobTitle',
    spec: {
      name: 'Job Title'
    }
  },
  {
    key: 'email',
    shownKey: 'email',
    spec: {
      name: 'Email'
    }
  },
  {
    key: 'phone',
    shownKey: 'phone',
    spec: {
      name: 'Phone Number'
    }
  },
  {
    key: 'company',
    shownKey: 'company',
    spec: {
      name: 'Company Name'
    }
  },
  {
    key: 'inquiryTypeSelect',
    shownKey: 'inquiryType',
    spec: {
      name: 'How can we help you?'
    }
  },
  {
    key: 'supportSelect',
    shownKey: 'support',
    spec: {
      name: 'Select the type of support you need.'
    }
  },
  {
    key: 'supportFreeform',
    shownKey: 'support',
    spec: {
      name: 'Please provide details so we can best assist you.'
    }
  },
  {
    key: 'inquiryProductSelect',
    shownKey: 'inquiryProduct',
    spec: {
      name: 'What product(s) are you interested in?'
    },
    transform(value) {
      return value && Array.isArray(value) && value.length > 0
        ? value.join(', ')
        : null
    }
  },
  {
    key: 'inquiryProductFreeform',
    shownKey: 'inquiryProduct',
    spec: {
      name: 'Explain what you are interested in.'
    }
  },
  {
    key: 'marketingSourceSelect',
    shownKey: 'marketingSource',
    spec: {
      name: 'How did you hear about us?'
    }
  },
  {
    key: 'marketingSourceFreeform',
    shownKey: 'marketingSource',
    spec: {
      name: 'Explain how you heard about us.'
    }
  },
  {
    key: 'inquiryFreeform',
    shownKey: 'inquiry',
    spec: {
      name: 'Please provide details so we can best assist you.'
    }
  }
]
const HS_FIELDS: ContactField[] = [
  {
    key: 'company',
    shownKey: 'company',
    spec: {
      name: 'company',
      objectTypeId: '0-1'
    }
  },
  {
    key: 'firstName',
    shownKey: 'firstName',
    spec: {
      name: 'firstname',
      objectTypeId: '0-1'
    }
  },
  {
    key: 'lastName',
    shownKey: 'lastName',
    spec: {
      name: 'lastname',
      objectTypeId: '0-1'
    }
  },
  {
    key: 'jobTitle',
    shownKey: 'jobTitle',
    spec: {
      name: 'jobtitle',
      objectTypeId: '0-1'
    }
  },
  {
    key: 'email',
    shownKey: 'email',
    spec: {
      name: 'email',
      objectTypeId: '0-1'
    }
  },
  {
    key: 'phone',
    shownKey: 'phone',
    spec: {
      name: 'phone',
      objectTypeId: '0-1'
    }
  },
  {
    key: 'inquiryTypeSelect',
    shownKey: 'inquiryType',
    spec: {
      name: 'inquiry_type_select',
      objectTypeId: '0-1'
    }
  },
  {
    key: 'inquiryFreeform',
    shownKey: 'inquiry',
    spec: {
      name: 'inquiry_freeform',
      objectTypeId: '0-1'
    }
  },
  {
    key: 'inquiryProductSelect',
    shownKey: 'inquiryProduct',
    spec: {
      name: 'inquiry_product_select',
      objectTypeId: '0-1'
    },
    transform(value) {
      return value && Array.isArray(value) && value.length > 0
        ? ';' + value.join(';')
        : null
    }
  },
  {
    key: 'inquiryProductFreeform',
    shownKey: 'inquiryProduct',
    spec: {
      name: 'inquiry_product_freeform',
      objectTypeId: '0-1'
    }
  },
  {
    key: 'marketingSourceSelect',
    shownKey: 'marketingSource',
    spec: {
      name: 'marketing_source_select',
      objectTypeId: '0-1'
    }
  },
  {
    key: 'marketingSourceFreeform',
    shownKey: 'marketingSource',
    spec: {
      name: 'marketing_source_freeform',
      objectTypeId: '0-1'
    }
  },
  {
    key: 'supportSelect',
    shownKey: 'support',
    spec: {
      name: 'support_select',
      objectTypeId: '0-1'
    }
  },
  {
    key: 'supportFreeform',
    shownKey: 'support',
    spec: {
      name: 'support_freeform',
      objectTypeId: '0-1'
    }
  }
]

const emit = defineEmits(['submitted'])

const props = defineProps({
  agreement: {
    default: undefined,
    type: Object
  },
  defaultInquiryProduct: {
    default: null,
    type: String
  },
  defaultInquiryType: {
    default: null,
    type: String
  },
  fields: {
    default: () => {},
    type: Object
  },
  hubspotFormId: {
    default: import.meta.env.VITE_HS_CONTACT_FORM_ID,
    type: String
  },
  hubspotPortalId: {
    default: import.meta.env.VITE_HS_CONTACT_PORTAL_ID,
    type: String
  },
  multiStep: {
    default: false,
    type: Boolean
  },
  required: {
    default: () => {},
    type: Object
  },
  submitText: {
    default: 'Submit',
    type: String
  }
})

const inquiryTypeItems = [
  { title: 'Customer Support', value: 'support' },
  { title: 'General Business Inquiry', value: 'general' }
]
const inquiryProductItems = [
  { title: 'Agile', value: 'agile' },
  { title: 'Group Training', value: 'group_training' },
  { title: 'Individual Training', value: 'individual_training' },
  { title: 'Leadership Training', value: 'leadership_training' },
  { title: 'Project Management', value: 'project_management' },
  { title: 'OKRs', value: 'okrs' },
  { title: 'Staffing', value: 'staffing' },
  { title: 'Transformation (Business/Digital)', value: 'transformation' },
  { title: 'Other (Please explain)', value: 'other' }
]
const marketingSourceItems = [
  { title: 'Google Search', value: 'google_search' },
  {
    title: 'Previous Hyperdrive Customer',
    value: 'previous_hyperdrive_customer'
  },
  { title: 'Referred', value: 'referred' },
  { title: 'Scrum Alliance', value: 'scrum_alliance' },
  { title: 'Social', value: 'social' },
  { title: 'PMI', value: 'pmi' },
  { title: 'Other (Please explain)', value: 'other' }
]
const supportItems = [
  { title: 'Reschedule Class', value: 'reschedule' },
  { title: 'Request for Receipt', value: 'receipt' },
  { title: 'Class Information', value: 'class_information' },
  { title: 'Alumni Benefits', value: 'alumni_benefits' },
  { title: 'Technical Support', value: 'technical_support' },
  { title: 'Other', value: 'other' }
]

const { canonicalPaths, siteConfig } = usePageContext()
const isSubmitted = ref(false)
const isSubmitError = ref(false)
const isValid = ref(true)
const form = ref(null)
const formData = reactive<{
  agreed: boolean
  company: string | null
  firstName: string | null
  lastName: string | null
  jobTitle: string | null
  email: string | null
  phone: string | null
  inquiryTypeSelect: string | null
  inquiryFreeform: string | null
  inquiryProductSelect: string[]
  inquiryProductFreeform: string | null
  marketingSourceSelect: string | null
  marketingSourceFreeform: string | null
  supportSelect: string | null
  supportFreeform: string | null
}>({
  agreed: false,
  company: null,
  firstName: null,
  lastName: null,
  jobTitle: null,
  email: null,
  phone: null,
  inquiryTypeSelect: props.defaultInquiryType || null,
  inquiryFreeform: null,
  inquiryProductSelect: props.defaultInquiryProduct
    ? [props.defaultInquiryProduct]
    : [],
  inquiryProductFreeform: null,
  marketingSourceSelect: null,
  marketingSourceFreeform: null,
  supportSelect: null,
  supportFreeform: null
})
type FormDataKey = keyof typeof formData
const step = ref(0)
const requiredFields = computed(() => {
  return Object.assign({}, REQUIRED_DEFAULT, props.required)
})
const shownFields = computed(() => {
  return Object.assign({}, FIELDS_DEFAULT, props.fields)
})
const formMailToHref = computed(() => {
  const body = EMAIL_FIELDS.map(({ key, spec, transform }) => ({
    value:
      typeof transform === 'function'
        ? transform(formData[key])
        : formData[key],
    ...spec
  }))
    .filter(({ value }) => value !== null)
    .map(({ name, value }) => `${name}:\n${value}`)
    .join('\n\n')
  return mailToHref({
    body,
    email: siteConfig.info_email,
    subject: 'Contact Form Request'
  })
})

function stepBack() {
  step.value--
}

function stepNext() {
  step.value++
}

function getCookieValue(name: string) {
  return document.cookie
    .split('; ')
    .find(cookie => cookie.startsWith(`${name}=`))
    ?.split('=')[1]
}

async function submit() {
  if (!form.value) return
  const result = await form.value.validate()
  if (!result.valid) return

  // Tidy up extraneous fields
  if (formData.inquiryTypeSelect === 'general') {
    formData.supportSelect = null
    formData.supportFreeform = null
  } else if (formData.inquiryTypeSelect === 'support') {
    formData.company = null
    formData.inquiryFreeform = null
    formData.inquiryProductSelect = []
    formData.inquiryProductFreeform = null
    formData.marketingSourceSelect = null
    formData.marketingSourceFreeform = null
  }

  const communications =
    props.agreement && Array.isArray(props.agreement.hubspot_subscription_types)
      ? props.agreement.hubspot_subscription_types.map(item => ({
          value: formData.agreed,
          subscriptionTypeId: item.id,
          text: props.agreement?.description
        }))
      : []

  try {
    const hubspotutk = getCookieValue('hubspotutk')
    const data = {
      fields: HS_FIELDS.map(({ key, spec, transform }) => ({
        value:
          typeof transform === 'function'
            ? transform(formData[key])
            : formData[key],
        ...spec
      })).filter(({ value }) => value !== null),
      legalConsentOptions: {
        consent: {
          communications,
          consentToProcess: true,
          text: siteConfig.contact_form.consent_text
        }
      },
      context: {
        hutk: hubspotutk,
        pageUri: canonicalPaths.absolute,
        pageName: canonicalPaths.relative
      }
    }

    if (!(props.hubspotFormId && props.hubspotPortalId)) {
      console.error('Hubspot form ID and portal ID are required')
      return
    }

    const resp = await fnSubmitContactForm(
      props.hubspotFormId,
      props.hubspotPortalId,
      data
    )
    if (resp.ok) {
      tracker.plausibleEvent('contactSend', {})

      if (formData.email && formData.firstName) {
        await tracker.posthogIdentify(formData.email, {
          name: formData.firstName
        })
        tracker.posthogCapture('lead:contact_form_submit', {})
      }

      tracker.googleEvent('submit_lead_form', {})

      const advertisingIdentity = await tracker.getAdvertisingIdentity(
        formData.email
      )
      tracker.facebookInit(advertisingIdentity)
      tracker.facebookEvent('Lead')
      tracker.googleAdsConversion('6oTxCKL30PgaEPPHwtgD', {
        user_data: {
          email: advertisingIdentity?.emailHash ?? null
        }
      })
    } else {
      isSubmitError.value = true
      tracker.plausibleEvent('contactError', {
        message: 'Success response not returned.'
      })
    }

    emit('submitted')
  } catch (err) {
    isSubmitError.value = true
    tracker.plausibleEvent('contactError', {}, err)
  } finally {
    isSubmitted.value = true
  }
}
</script>
